import React from "react";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { api, CATEGORIES, CATEGORY_LABEL, resolvePhotoUrl } from "../lib/api";
import SEO from "../components/site/SEO";

export default function Galerie() {
    const [searchParams, setSearchParams] = useSearchParams();
    const initial = searchParams.get("cat") || "all";
    const [active, setActive] = React.useState(initial);
    const [photos, setPhotos] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [lightbox, setLightbox] = React.useState(null);

    React.useEffect(() => {
        setLoading(true);
        const params = active === "all" ? {} : { category: active };
        api.get("/photos", { params })
            .then((r) => setPhotos(r.data))
            .finally(() => setLoading(false));
        if (active === "all") {
            searchParams.delete("cat");
        } else {
            searchParams.set("cat", active);
        }
        setSearchParams(searchParams, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    React.useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") setLightbox(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    return (
        <div data-testid="page-galerie" className="pt-32 pb-24 px-6 md:px-12 lg:px-24">
            <SEO title="Galeries" description="Découvrez les galeries photo : automobile, mariage, baptême et portraits. Style cinématographique signé paul.mv07." />
            <div className="mb-16">
                <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">— Portfolio</p>
                <h1 className="font-serif-display text-5xl md:text-7xl">Galeries</h1>
                <p className="text-zinc-400 mt-6 max-w-2xl leading-relaxed">
                    Une sélection de mes travaux récents. Filtrez par univers, ou laissez-vous porter.
                </p>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-12 border-b border-white/10 pb-6">
                <button
                    data-testid="gallery-filter-all"
                    onClick={() => setActive("all")}
                    className={`px-5 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                        active === "all"
                            ? "bg-[#c5a059] text-black border-[#c5a059]"
                            : "border-white/15 text-zinc-300 hover:text-white hover:border-white/40"
                    }`}
                >
                    Tous
                </button>
                {CATEGORIES.map((c) => (
                    <button
                        key={c.key}
                        data-testid={`gallery-filter-${c.key}`}
                        onClick={() => setActive(c.key)}
                        className={`px-5 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                            active === c.key
                                ? "bg-[#c5a059] text-black border-[#c5a059]"
                                : "border-white/15 text-zinc-300 hover:text-white hover:border-white/40"
                        }`}
                    >
                        {c.label}
                    </button>
                ))}
            </div>

            {/* Grid */}
            {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="aspect-[3/4] bg-zinc-900 animate-pulse" />
                    ))}
                </div>
            ) : photos.length === 0 ? (
                <div data-testid="gallery-empty" className="text-center py-32 text-zinc-500">
                    Aucune photo pour cette catégorie.
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
                    {photos.map((p, i) => (
                        <button
                            key={p.id}
                            data-testid={`gallery-item-${i}`}
                            onClick={() => setLightbox(p)}
                            className="photo-tile relative aspect-[3/4] overflow-hidden border border-white/5 group"
                        >
                            <img src={resolvePhotoUrl(p.url)} alt={p.title || ""} className="w-full h-full object-cover" loading="lazy" />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                            {p.title && (
                                <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-black/80 to-transparent">
                                    <p className="text-xs uppercase tracking-[0.2em] text-[#c5a059]">
                                        {CATEGORY_LABEL[p.category]}
                                    </p>
                                    <p className="text-white font-serif-display text-xl">{p.title}</p>
                                </div>
                            )}
                        </button>
                    ))}
                </div>
            )}

            {/* Lightbox */}
            {lightbox && (
                <div
                    data-testid="lightbox"
                    onClick={() => setLightbox(null)}
                    className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-6 cursor-zoom-out"
                >
                    <button
                        data-testid="lightbox-close"
                        onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
                        className="absolute top-6 right-6 text-white hover:text-[#c5a059] transition-colors"
                        aria-label="Fermer"
                    >
                        <X size={32} strokeWidth={1.5} />
                    </button>
                    <img
                        src={resolvePhotoUrl(lightbox.url)}
                        alt={lightbox.title || ""}
                        className="max-h-full max-w-full object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />
                    {lightbox.title && (
                        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center">
                            <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">
                                {CATEGORY_LABEL[lightbox.category]}
                            </p>
                            <p className="font-serif-display text-2xl text-white mt-1">{lightbox.title}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
